import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import {
    getLogger,
    DistributionListMember,
    runOwsRequestWithMonitor,
    Feature,
} from '../../../shared';
import { contains, areMemberArraysEqual } from '../../utils/privateDLComparer';
import { UpdatePersonaRequest, PersonaType, getServiceAsync, TFactories } from '../../../service';
import { Md5 } from 'ts-md5/dist/md5';

export type UpdatePrivateDLData = {
    personaId: string;
    oldName?: string;
    newName?: string;
    oldNotes?: string;
    newNotes?: string;
    oldMembers?: DistributionListMember[];
    newMembers?: DistributionListMember[];
    parentFolderId: string;
};

export type AddMembersToPrivateDLData = {
    personaId: string;
    membersToAdd: DistributionListMember[];
};

export type RemoveMembersFromPrivateDLData = {
    personaId: string;
    membersToRemove: DistributionListMember[];
};

function buildUpdateRequest(
    data: UpdatePrivateDLData,
    serviceFactory: TFactories
): UpdatePersonaRequest {
    const request = serviceFactory.updatePersonaRequest({
        PersonaId: serviceFactory.itemId({ Id: data.personaId }),
        PersonTypeString: 'DistributionList',
        PropertyUpdates: [],
    });

    // Parent folder is needed for Public Folders
    if (data.parentFolderId) {
        request.ParentFolderId = serviceFactory.targetFolderId({
            BaseFolderId: serviceFactory.folderId({ Id: data.parentFolderId }),
        });
    }

    if (data.newName !== undefined && data.newName !== data.oldName) {
        request.PropertyUpdates.push(
            serviceFactory.personaPropertyUpdate({
                Path: serviceFactory.propertyUri({ FieldURI: 'PersonaDisplayNames' }),
                OldValue: data.oldName || '',
                NewValue: data.newName,
            })
        );
    }

    if (data.newNotes !== undefined && data.newNotes !== data.oldNotes) {
        request.PropertyUpdates.push(
            serviceFactory.personaPropertyUpdate({
                Path: serviceFactory.propertyUri({ FieldURI: 'PersonaBodies' }),
                OldValue: data.oldNotes || '',
                NewValue: data.newNotes,
            })
        );
    }

    if (!(data.oldMembers && data.newMembers)) {
        const logger = getLogger();
        logger.log('Update.UndefinedPdlMembers', {
            variableName: !(data.oldMembers || data.newMembers)
                ? 'both'
                : !data.oldMembers
                ? 'oldMembers'
                : 'newMembers',
        });
    }

    if (!areMemberArraysEqual(data.oldMembers, data.newMembers)) {
        data.newMembers
            .filter(m => !contains(m, data.oldMembers))
            .forEach(newMember => {
                if (newMember.isManuallyTypedEmail) {
                    // Manually typed email address
                    request.PropertyUpdates.push({
                        Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                        AddMemberToPDL: {
                            Name: newMember.displayName,
                            EmailAddress: newMember.emailAddressWrapper.EmailAddress,
                        },
                    });
                } else {
                    // Person or a PDL added via the PeoplePicker via "Edit" a PDL
                    request.PropertyUpdates.push(
                        serviceFactory.personaPropertyUpdate({
                            Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                            AddMemberToPDL: newMember.emailAddressWrapper,
                        })
                    );
                }
            });

        data.oldMembers
            .filter(m => !contains(m, data.newMembers))
            .forEach(oldMember => {
                request.PropertyUpdates.push(
                    serviceFactory.personaPropertyUpdate({
                        Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                        DeleteMemberFromPDL: oldMember.emailAddressWrapper,
                    })
                );
            });
    }

    if (request.PropertyUpdates.length === 0) {
        // This should never happen. Here we log the different values to be able to debug further

        const getStringValue = (value: string) => {
            return value === undefined
                ? 'undefined'
                : value === null
                ? 'null'
                : value === ''
                ? 'empty'
                : (Md5.hashAsciiStr(value) as string);
        };

        const getArrayValue = (array: DistributionListMember[]) => {
            return array === undefined
                ? 'undefined'
                : array === null
                ? 'null'
                : array.length === 0
                ? 'empty'
                : JSON.stringify(
                      array.reduce((result, value) => {
                          return [
                              ...result,
                              {
                                  id: getStringValue(
                                      value.isPdl
                                          ? value.emailAddressWrapper.ItemId.Id
                                          : value.emailAddressWrapper.EmailAddress
                                  ),
                                  displayName: getStringValue(value.displayName),
                                  isPdl: value.isPdl,
                                  isManuallyTypedEmail: value.isManuallyTypedEmail,
                              },
                          ];
                      }, [])
                  );
        };

        const logger = getLogger();
        logger.log('UpdatePrivateDL.PropertyUpdatesEmpty', {
            oldName: getStringValue(data.oldName),
            newName: getStringValue(data.newName),
            oldNotes: getStringValue(data.oldNotes),
            newNotes: getStringValue(data.newNotes),
            oldMembers: getArrayValue(data.oldMembers),
            newMembers: getArrayValue(data.newMembers),
        });
    }

    return request;
}

function buildAddRequest(
    data: AddMembersToPrivateDLData,
    serviceFactory: TFactories
): UpdatePersonaRequest {
    const request = serviceFactory.updatePersonaRequest({
        PersonaId: serviceFactory.itemId({ Id: data.personaId }),
        PersonTypeString: 'DistributionList',
        PropertyUpdates: [],
    });

    data.membersToAdd.forEach(member => {
        if (member.isManuallyTypedEmail) {
            request.PropertyUpdates.push({
                Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                AddMemberToPDL: {
                    Name: member.displayName,
                    EmailAddress: member.emailAddressWrapper.EmailAddress,
                },
            });
        } else {
            request.PropertyUpdates.push(
                serviceFactory.personaPropertyUpdate({
                    Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                    AddMemberToPDL: member.emailAddressWrapper,
                })
            );
        }
    });

    return request;
}

function buildRemoveRequest(
    data: RemoveMembersFromPrivateDLData,
    serviceFactory: TFactories
): UpdatePersonaRequest {
    const request = serviceFactory.updatePersonaRequest({
        PersonaId: serviceFactory.itemId({ Id: data.personaId }),
        PersonTypeString: 'DistributionList',
        PropertyUpdates: [],
    });

    data.membersToRemove.forEach(member => {
        request.PropertyUpdates.push(
            serviceFactory.personaPropertyUpdate({
                Path: serviceFactory.propertyUri({ FieldURI: 'PersonaMembers' }),
                DeleteMemberFromPDL: member.emailAddressWrapper,
            })
        );
    });

    return request;
}

export function updatePrivateDL(data: UpdatePrivateDLData): Promise<void> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'UpdatePrivateDL',
            feature: Feature.PrivateDistributionList,
            operation: () => {
                return service.Operation.updatePersonaOperation({
                    request: {
                        Header: getJsonRequestHeader(),
                        Body: buildUpdateRequest(data, service.Factory),
                    },
                });
            },
            transformResponse: (_personaType: PersonaType) => {
                return;
            },
        })
    );
}

export function addMembersToPrivateDL(data: AddMembersToPrivateDLData): Promise<void> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'addMembersToPrivateDL',
            feature: Feature.ContactListsInCommandBar,
            operation: () => {
                return service.Operation.updatePersonaOperation({
                    request: {
                        Header: getJsonRequestHeader(),
                        Body: buildAddRequest(data, service.Factory),
                    },
                });
            },
            transformResponse: (_personaType: PersonaType) => {
                return;
            },
        })
    );
}

export function removeMembersFromPrivateDL(data: RemoveMembersFromPrivateDLData): Promise<void> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'removeMembersFromPrivateDL',
            feature: Feature.ContactListsInCommandBar,
            operation: () => {
                return service.Operation.updatePersonaOperation({
                    request: {
                        Header: getJsonRequestHeader(),
                        Body: buildRemoveRequest(data, service.Factory),
                    },
                });
            },
            transformResponse: (_personaType: PersonaType) => {
                return;
            },
        })
    );
}
